import React from 'react';

/**
 * ResponsiveContainer
 *
 * A centralized container component that enforces consistent responsive padding
 * and max-width constraints across the application.
 *
 * Benefits:
 * - Consistent spacing at all breakpoints
 * - No more manual max-w-* + px-* combinations
 * - Easy to update globally if design system changes
 *
 * Usage:
 * ```tsx
 * <ResponsiveContainer size="lg">
 *   <YourContent />
 * </ResponsiveContainer>
 * ```
 */

interface ResponsiveContainerProps {
  children: React.ReactNode;
  /**
   * Container max-width size
   * - 'sm': 640px (forms, narrow content)
   * - 'md': 768px (default, general content)
   * - 'lg': 1024px (wide content)
   * - 'xl': 1280px (very wide content)
   * - 'full': 100% width
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /**
   * Apply responsive padding
   * Default: true (applies px-4 sm:px-6 lg:px-8)
   */
  padding?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Center the container horizontally */
  center?: boolean;
}

export default function ResponsiveContainer({
  children,
  size = 'lg',
  padding = true,
  className = '',
  center = true,
}: ResponsiveContainerProps) {
  const sizeClasses = {
    sm: 'max-w-2xl',      // 640px
    md: 'max-w-3xl',      // 768px
    lg: 'max-w-7xl',      // 1024px
    xl: 'max-w-screen-xl', // 1280px
    full: 'max-w-full',   // 100%
  };

  const paddingClass = padding ? 'px-4 sm:px-6 lg:px-8' : '';
  const centerClass = center ? 'mx-auto' : '';

  return (
    <div className={`${sizeClasses[size]} ${paddingClass} ${centerClass} ${className}`}>
      {children}
    </div>
  );
}
